"use client";

import {
  createContext,
  useContext,
  useRef,
  useState,
  type ComponentProps,
} from "react";
import { ExpandIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ZoomImage } from "./record-image-viewer";
import { cn } from "@/lib/utils";

// Linked images retain their author's destination, without nested buttons.
export const MarkdownImageLinkContext = createContext(false);

export function attachedImageId(src: string | undefined): string | null {
  if (!src) return null;
  const match = /^\/api\/client\/files\/([^/?#]+)$/.exec(src);
  if (!match) return null;
  try {
    const id = decodeURIComponent(match[1] ?? "");
    return id && id !== "." && id !== ".." && !/[\/\\\u0000-\u001f]/.test(id)
      ? id
      : null;
  } catch {
    return null;
  }
}

/**
 * An answer image the browser can fetch: an absolute web address, inline
 * data, or a path on this site. A bare file name would resolve against the
 * page and never load.
 */
export function loadableImageUrl(src: string | undefined): boolean {
  if (!src) return false;
  return /^(?:https?:|data:image\/|blob:)/iu.test(src) || src.startsWith("/");
}

function UnavailableImage({ alt }: { alt?: string }) {
  return (
    <span
      role="status"
      className="not-typeset my-2 inline-flex max-w-sm items-center rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
    >
      Image unavailable{alt?.trim() ? `: ${alt.trim()}` : ""}
    </span>
  );
}

function PlainMarkdownImage({ src, alt, title }: { src?: string; alt?: string; title?: string }) {
  const [failed, setFailed] = useState(false);
  if (failed || !loadableImageUrl(src)) return <UnavailableImage alt={alt} />;
  return (
    <img
      src={src}
      alt={alt ?? ""}
      title={title}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

/** Presentation only; rendering a photograph does not attest model inspection. */
export function MarkdownImage({ src, alt, title }: ComponentProps<"img">) {
  const linked = useContext(MarkdownImageLinkContext);
  const url = typeof src === "string" ? src : undefined;
  const fileId = attachedImageId(url);
  if (!fileId || linked)
    return <PlainMarkdownImage src={url} alt={alt} title={title} />;
  return (
    <AttachedMarkdownImage
      key={fileId}
      fileId={fileId}
      alt={alt}
      title={title}
    />
  );
}

function AttachedMarkdownImage({
  fileId,
  alt,
  title,
}: {
  fileId: string;
  alt?: string;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const label = alt?.trim() || "Attached image";
  const src = `/api/client/files/${encodeURIComponent(fileId)}`;
  return (
    <>
      <span className="not-typeset my-2 inline-block w-full max-w-sm align-top">
        <button
          ref={trigger}
          type="button"
          aria-label={`Enlarge ${label}`}
          disabled={failed}
          onClick={() => setOpen(true)}
          className={cn(
            "relative block w-full overflow-hidden rounded-lg border border-border bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99] disabled:cursor-default",
            !failed && "aspect-[4/3]",
          )}
        >
          {failed ? (
            <span
              role="status"
              className="flex min-h-24 items-center justify-center p-4 text-sm text-muted-foreground"
            >
              Image unavailable. Access may have changed or the file could not
              be loaded.
            </span>
          ) : (
            <img
              src={src}
              alt={label}
              title={title}
              loading="lazy"
              decoding="async"
              onError={() => setFailed(true)}
              className="!m-0 absolute inset-0 block !h-full !w-full object-contain"
            />
          )}
          {!failed ? (
            <span
              aria-hidden
              className="absolute bottom-2 right-2 rounded-md bg-background/90 p-1.5"
            >
              <ExpandIcon className="size-4" />
            </span>
          ) : null}
        </button>
        <span className="mt-1 flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
          <span className="min-w-0 break-words">{label}</span>
          <a
            href={src}
            aria-label={`Open original ${label}`}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-link underline"
          >
            Open original
          </a>
        </span>
      </span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          finalFocus={trigger}
          className="max-w-5xl"
          onKeyDown={(event) => event.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>
              Attached image · display is not evidence of AI analysis
            </DialogDescription>
          </DialogHeader>
          {open ? (
            <ZoomImage
              image={{ id: fileId, fileId, label, fileName: title || label }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
